/**
 * Empresa que un usuario de backoffice está gestionando desde el área de
 * empresa (`/company/*`).
 *
 * Las empresas siempre las crea backoffice y backoffice puede administrarlas
 * todas, pero no pertenece a ninguna: sus llamadas a `/companies/me/*`
 * necesitan un `companyId` explícito. Aquí se guarda cuál está "abierta".
 *
 * Vive en sessionStorage para sobrevivir a una recarga sin arrastrarse a la
 * siguiente sesión. Para company_admin siempre es `null` (el backend ignora el
 * parámetro en ese caso).
 */

const STORAGE_KEY = 'knooowy.companyScope';

export interface CompanyScope {
  id: string;
  name: string;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let cache: CompanyScope | null | undefined;

function read(): CompanyScope | null {
  if (cache !== undefined) return cache;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    cache = raw ? (JSON.parse(raw) as CompanyScope) : null;
  } catch {
    cache = null;
  }
  return cache;
}

function emit(): void {
  for (const listener of listeners) listener();
}

export function getCompanyScope(): CompanyScope | null {
  return read();
}

export function setCompanyScope(scope: CompanyScope): void {
  cache = scope;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // sessionStorage no disponible (modo privado): el scope vive solo en memoria.
  }
  emit();
}

export function clearCompanyScope(): void {
  cache = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // idem
  }
  emit();
}

export function subscribeCompanyScope(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Añade `companyId` a una ruta de `/companies/me/*`.
 *
 * Usa el `companyId` explícito si se pasa (backoffice consultando una empresa
 * concreta sin entrar en ella, p. ej. el detalle de empresa) y si no, la
 * empresa abierta. Sin ninguna de las dos, la ruta se deja tal cual.
 */
export function withCompanyScope(path: string, companyId?: string): string {
  const id = companyId ?? read()?.id;
  if (!id) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}companyId=${encodeURIComponent(id)}`;
}
